import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NotificationPanel = () => {
  const [notifications, setNotifications] = useState([
    {
      id: 1,
      type: 'achievement',
      title: 'Objectif mensuel atteint !',
      message: 'Félicitations ! Vous avez dépassé votre objectif de commissions pour septembre 2024.',
      timestamp: new Date('2024-09-06T19:15:00'),
      isRead: false,
      priority: 'high'
    },
    {
      id: 2,
      type: 'payment',
      title: 'Paiement en cours de traitement',
      message: 'Votre paiement de 1.250,75 € sera versé dans 2-3 jours ouvrables.',
      timestamp: new Date('2024-09-06T16:30:00'),
      isRead: false,
      priority: 'medium'
    },
    {
      id: 3,
      type: 'system',
      title: 'Nouvelles fonctionnalités disponibles',
      message: 'Découvrez les nouveaux outils d\'analyse et de reporting dans votre tableau de bord.',
      timestamp: new Date('2024-09-06T14:20:00'),
      isRead: true,
      priority: 'low'
    },
    {
      id: 4,
      type: 'booking',
      title: 'Nouvelle demande de réservation',
      message: 'Une venue souhaite réserver DJ Alex Martin pour le 15 septembre.',
      timestamp: new Date('2024-09-06T12:45:00'),
      isRead: true,
      priority: 'high'
    }
  ]);

  const [showAll, setShowAll] = useState(false);

  const markAsRead = (id) => {
    setNotifications(prev => 
      prev?.map(notif => 
        notif?.id === id ? { ...notif, isRead: true } : notif
      )
    );
  };

  const dismissNotification = (id) => {
    setNotifications(prev => prev?.filter(notif => notif?.id !== id));
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'achievement': return 'Trophy';
      case 'payment': return 'CreditCard';
      case 'system': return 'Settings';
      case 'booking': return 'Calendar';
      default: return 'Bell';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'border-l-error';
      case 'medium': return 'border-l-warning';
      case 'low': return 'border-l-accent';
      default: return 'border-l-muted';
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })?.format(timestamp);
  };

  const unreadCount = notifications?.filter(n => !n?.isRead)?.length;
  const displayedNotifications = showAll ? notifications : notifications?.slice(0, 3);

  return (
    <div className="bg-card rounded-lg border border-border shadow-sm">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <h3 className="text-lg font-semibold text-card-foreground">Notifications</h3>
            {unreadCount > 0 && (
              <span className="bg-error text-error-foreground text-xs px-2 py-1 rounded-full">
                {unreadCount}
              </span>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName="Settings"
            iconSize={16}
            onClick={() => console.log('Open notification settings')}
          />
        </div>
      </div>
      <div className="max-h-80 overflow-y-auto">
        {displayedNotifications?.length === 0 ? (
          <div className="p-6 text-center">
            <Icon name="Bell" size={48} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">Aucune notification</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {displayedNotifications?.map((notification) => (
              <div
                key={notification?.id}
                className={`p-4 border-l-4 ${getPriorityColor(notification?.priority)} ${
                  !notification?.isRead ? 'bg-accent/5' : ''
                } hover:bg-muted/50 transition-colors duration-150`}
              >
                <div className="flex items-start justify-between space-x-3">
                  <div className="flex items-start space-x-3 flex-1">
                    <div className="flex items-center justify-center w-8 h-8 bg-accent/10 rounded-full">
                      <Icon name={getNotificationIcon(notification?.type)} size={16} className="text-accent" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <p className={`text-sm font-medium ${!notification?.isRead ? 'text-card-foreground' : 'text-muted-foreground'}`}>
                          {notification?.title}
                        </p>
                        {!notification?.isRead && (
                          <div className="w-2 h-2 bg-accent rounded-full"></div>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {notification?.message}
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        {formatTimestamp(notification?.timestamp)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    {!notification?.isRead && (
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Check"
                        iconSize={14}
                        onClick={() => markAsRead(notification?.id)}
                        className="text-muted-foreground hover:text-success"
                      />
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="X"
                      iconSize={14}
                      onClick={() => dismissNotification(notification?.id)}
                      className="text-muted-foreground hover:text-error"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {notifications?.length > 3 && (
        <div className="p-4 border-t border-border">
          <Button
            variant="ghost"
            size="sm"
            fullWidth
            iconName={showAll ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? 'Voir moins' : `Voir ${notifications?.length - 3} notifications de plus`}
          </Button>
        </div>
      )}
    </div>
  );
};

export default NotificationPanel;